import { useSubjectMember } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { useEffect, useRef } from 'react'

import { mixpanel } from '@/mixpanel'
import { useHylyght } from '@/modules/hylyght/hooks/useHylyght'
import { openCozo, openUrl, openVidis } from '@/utils/external-resources'
import { useFeatureFlag } from '@/utils/feature-flag'
import { useInvoiceLink, useMealLink } from '@/utils/navigation'

import type { IconProp } from '@fortawesome/fontawesome-svg-core'

export type HomeShortcut = {
  title: string
  icon: IconProp
  link?: string
  onClick?: () => void
}

export const useGetHomeShortcuts = (): HomeShortcut[] => {
  const hasFetchedHylyghtMagicLink = useRef<boolean>(false)

  // Hooks
  // ------------------------------------------------------------------------- /
  const mealLink = useMealLink()?.url
  const subject = useSubjectMember()

  const {
    getHylyghtAthleteMagicLink: {
      data: getHylyghtMagicLinkResponse,
      isLoading: hylyghtMagicLinkLoading,
      mutate: getHylyghtMagicLink,
    },
  } = useHylyght()

  // Template variables
  // ------------------------------------------------------------------------- /
  const hylyghtMagicLink = getHylyghtMagicLinkResponse ?? subject?.meta?.hylyghtMagicLink

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (
      !subject?.meta.hylyghtAthleteId ||
      !hylyghtMagicLink ||
      hylyghtMagicLinkLoading ||
      !subject ||
      hasFetchedHylyghtMagicLink.current
    ) {
      return
    }

    if (!hylyghtMagicLink?.expires || hylyghtMagicLink.expires <= Math.floor(Date.now() / 1000)) {
      getHylyghtMagicLink()
      hasFetchedHylyghtMagicLink.current = true
    }
  }, [hylyghtMagicLink, getHylyghtMagicLink, hylyghtMagicLinkLoading, subject])

  const invoiceLink = useInvoiceLink()?.url
  const careQuestionEnabled = useFeatureFlag('care_question')
  const healthParametersEnabled = useFeatureFlag('health_parameters')

  const shortcutItems: HomeShortcut[] = [
    {
      title: t({
        id: 'home.action-grid-section.shortcuts.appointment',
        message: 'Nieuwe afspraak',
      }),
      icon: ['fal', 'calendar-plus'],
      link: '/appointment/book',
    },
  ]
  if (healthParametersEnabled) {
    shortcutItems.push({
      title: t({
        id: 'home.action-grid-section.shortcuts.health-data',
        message: 'Gezondheids-gegevens',
      }),
      icon: ['fal', 'folder-medical'],
      link: '/profile/health',
    })
  }
  if (careQuestionEnabled) {
    shortcutItems.push({
      title: t({
        id: 'home.action-grid-section.shortcuts.care-question',
        message: 'Een vraag stellen',
      }),
      icon: ['fal', 'question-circle'],
      link: '/care-question/question',
    })
  }

  if (mealLink) {
    shortcutItems.push({
      title: t({
        id: 'home.action-grid-section.shortcuts.cmeal',
        message: 'Maaltijden',
      }),
      icon: ['fal', 'salad'],
      onClick: () => {
        mixpanel.track('home_shortcut_cmeal')
        openUrl(mealLink, { windowName: '_blank' })
      },
    })
  }

  // Make sure they have a valid invoiceLink
  if (invoiceLink) {
    shortcutItems.push({
      title: t({
        id: 'home.action-grid-section.shortcuts.invoices',
        message: 'Facturen',
      }),
      icon: ['fal', 'file-invoice'],
      onClick: () => {
        mixpanel.track('home_shortcut_invoices')
        openUrl(invoiceLink, { windowName: '_blank' })
      },
    })
  }

  shortcutItems.push(
    {
      title: t({
        id: 'home.action-grid-section.shortcuts.medical-data',
        message: 'Medische resultaten',
      }),
      icon: ['fal', 'file-medical'],
      onClick: () => {
        mixpanel.track('home_shortcut_medical_results')
        openCozo()
      },
    },
    {
      title: t({
        id: 'home.action-grid-section.shortcuts.prescriptions',
        message: 'Voorschriften',
      }),
      icon: ['fal', 'prescription-bottle'],
      onClick: () => {
        mixpanel.track('home_shortcut_prescriptions')
        openVidis()
      },
    },
  )

  if (hylyghtMagicLink?.link) {
    if (hylyghtMagicLink.expires < Math.floor(Date.now() / 1000)) {
      return shortcutItems
    }
    shortcutItems.push({
      title: t({
        id: 'home.action-grid-section.shortcuts.hylyght',
        message: 'Hylyght',
      }),
      icon: ['fal', 'person-walking'],
      onClick: () => {
        mixpanel.track('home_shortcut_hylyght')
        openUrl(hylyghtMagicLink.link, { windowName: '_blank' })
      },
    })
  }

  return shortcutItems
}
